/**
 * Which catalog row a device's reported board belongs to.
 *
 * The dashboard identifies hardware by the raw string the router reports
 * ('x86-64,lenovo-qwfq9798', 'ruijie,rg-ew1200g-pro-v1.1'); the device catalog
 * is keyed by the shorter ids an admin edits ('x86-64', 'ruijie-rg-ew1200g-pro-v1.1').
 * This is the one bridge between them.
 *
 * 🔴 Display only. Nothing that DECIDES anything — the fleet filter's board
 * kind, a firmware target, a wizard's flash file — goes through the catalog,
 * so renaming a product in the admin screen can never change behaviour. That
 * was the trap in the old label maps: the filter compared display strings, so
 * a rename broke it.
 */
export const CATALOG_ID_BY_BOARD: ReadonlyArray<readonly [RegExp, string]> = [
   [/ruijie/i, 'ruijie-rg-ew1200g-pro-v1.1'],
   // The PC (H3, 1GB) and the Zero 3 (H618) are their OWN images: exact ids,
   // listed BEFORE the generic OrangePi row so it cannot swallow them.
   [/^xunlong,orangepi-pc$/i, 'orangepi-pc'],
   [/^xunlong,orangepi-zero3$/i, 'orangepi-zero3'],
   [/orangepi|xunlong/i, 'orangepi-one'],
   [/cf-ew71/i, 'comfast-cf-ew71-v2'],
   [/cf-ew72/i, 'comfast-cf-ew72-v2'],
   [/clt-r30b1/i, 'rt2980'],
   [/ep-rt2983|netis,n6/i, 'rt2983'],
   [/newifi-d2/i, 'newifi-d2'],
   [/^360,(360)?t6m$/i, '360-t6m'],
   [/^x86-64,/i, 'x86-64'],
];

export function catalogIdForHwBoard(hwBoard?: string): string | undefined {
   if (!hwBoard) return undefined;
   for (const [re, id] of CATALOG_ID_BY_BOARD) if (re.test(hwBoard)) return id;
   return undefined;
}

/** The fleet filter's own kinds, for the chip labels only. */
export const CATALOG_ID_BY_BOARD_KIND: Readonly<Record<string, string>> = {
   ruijie: 'ruijie-rg-ew1200g-pro-v1.1',
   orangepi: 'orangepi-one',
   orangepipc: 'orangepi-pc',
   orangepizero3: 'orangepi-zero3',
   comfast: 'comfast-cf-ew71-v2',
   comfast72: 'comfast-cf-ew72-v2',
   rt2980: 'rt2980',
   rt2983: 'rt2983',
   newifid2: 'newifi-d2',
   t6m: '360-t6m',
   minipc: 'x86-64',
};

/**
 * The cards on "Choose your device" in the register wizard. Same ids as the
 * fleet kinds, plus MikroTik — which is a card in the wizard and a row in the
 * catalog even though no MikroTik runs our own image (Ray, 2026-09-24: "why
 * theres no mikrotik?").
 */
export const CATALOG_ID_BY_CHOOSER_CARD: Readonly<Record<string, string>> = {
   ...CATALOG_ID_BY_BOARD_KIND,
   mikrotik: 'mikrotik',
};
